import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Plus, Trash2, PoundSterling, Send,
  CheckCircle2, Clock, AlertCircle, Save, Download,
  Trophy, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { generateQuotePDF, downloadPDF } from '../utils/pdfExport';
import { CalendarExportButton } from '../components/CalendarExport';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const MEP_DISCIPLINES = [
  { id: 'mechanical', label: 'Mechanical' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'fire', label: 'Fire Protection' },
];

const DEFAULT_LINE_ITEM = {
  description: '',
  discipline: 'mechanical',
  quantity: 1,
  unit: 'nr',
  unit_price: 0,
  total: 0,
};

export const QuotePage = () => {
  // Support both /enquiry/:enquiryId/quote and /quotes/:quoteId routes
  const { enquiryId: enquiryIdParam, quoteId: quoteIdParam } = useParams();
  const navigate = useNavigate();
  const { user, authFetch } = useAuth();
  
  // Track the resolved enquiryId (may come from URL or from quote data)
  const [resolvedEnquiryId, setResolvedEnquiryId] = useState(enquiryIdParam || null);
  const [enquiry, setEnquiry] = useState(null);
  const [decision, setDecision] = useState(null);
  const [quote, setQuote] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [wonNotes, setWonNotes] = useState('');
  const [lostReason, setLostReason] = useState('');
  
  // Send to Client state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [customMessage, setCustomMessage] = useState('Thank you for the opportunity to provide this quotation. Please find our proposal attached as a PDF document.\n\nWe would welcome the opportunity to discuss this further at your convenience.');
  const [canSendQuote, setCanSendQuote] = useState(false);
  const [_sendError, setSendError] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [includeApprovalButtons, setIncludeApprovalButtons] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendStep, setSendStep] = useState(1); // 1 = details, 2 = confirm
  
  const [lineItems, setLineItems] = useState([{ ...DEFAULT_LINE_ITEM }]);
  const [vatRate, setVatRate] = useState(20);
  const [validityDays, setValidityDays] = useState(30);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Standard payment terms: 30 days from invoice date.\nThis quote is valid for the period stated above.\nAll prices exclude VAT unless otherwise stated.');

  const loadData = useCallback(async () => {
    try {
      let currentEnquiryId = enquiryIdParam;
      let existingQuote = null;
      
      // If accessed via /quotes/:quoteId, first load the quote to get the enquiry_id
      if (quoteIdParam && !enquiryIdParam) {
        const quoteByIdRes = await authFetch(`/quotes/${quoteIdParam}`);
        if (quoteByIdRes.ok) {
          existingQuote = await quoteByIdRes.json();
          currentEnquiryId = existingQuote.enquiry_id;
          setResolvedEnquiryId(currentEnquiryId);
          setQuote(existingQuote);
          setLineItems(existingQuote.line_items || [{ ...DEFAULT_LINE_ITEM }]);
          setVatRate(existingQuote.vat_rate ?? 20);
          setValidityDays(existingQuote.validity_days || 30);
          setQuoteNumber(existingQuote.quote_number || '');
          setNotes(existingQuote.notes || '');
          setTerms(existingQuote.terms_conditions || terms);
          
          // Check if quote can be sent to client
          const canSendRes = await authFetch(`/quotes/${existingQuote.id}/can-send`);
          if (canSendRes.ok) {
            const sendCheck = await canSendRes.json();
            setCanSendQuote(sendCheck.can_send);
            setSendError(sendCheck.error || null);
          }
        } else {
          toast.error('Quote not found');
          navigate('/dashboard');
          return;
        }
      }
      
      // Now we have currentEnquiryId - load enquiry
      if (currentEnquiryId) {
        const enquiryRes = await authFetch(`/enquiries/${currentEnquiryId}`);
        if (enquiryRes.ok) {
          const data = await enquiryRes.json();
          setEnquiry(data);
        }
      }
      
      // Load branding for PDF
      const brandingRes = await authFetch('/team/branding');
      if (brandingRes.ok) {
        const data = await brandingRes.json();
        setBranding(data.branding || data);
      }
      
      // If we haven't loaded the quote yet (came via enquiryId route), try to load it
      if (!existingQuote && currentEnquiryId) {
        const quoteRes = await authFetch(`/quotes/enquiry/${currentEnquiryId}`);
        if (quoteRes.ok) {
          existingQuote = await quoteRes.json();
          setQuote(existingQuote);
          setLineItems(existingQuote.line_items || [{ ...DEFAULT_LINE_ITEM }]);
          setVatRate(existingQuote.vat_rate ?? 20);
          setValidityDays(existingQuote.validity_days || 30);
          setQuoteNumber(existingQuote.quote_number || '');
          setNotes(existingQuote.notes || '');
          setTerms(existingQuote.terms_conditions || terms);
          
          // Check if quote can be sent to client
          if (existingQuote.id) {
            const canSendRes = await authFetch(`/quotes/${existingQuote.id}/can-send`);
            if (canSendRes.ok) {
              const sendCheck = await canSendRes.json();
              setCanSendQuote(sendCheck.can_send);
              setSendError(sendCheck.error || null);
            }
          }
        }
      }
      
      // Load decision
      if (currentEnquiryId) {
        const decisionRes = await authFetch(`/decisions/enquiry/${currentEnquiryId}`);
        if (decisionRes.ok) {
          const data = await decisionRes.json();
          setDecision(data);
          
          // ONLY redirect if NO quote exists AND decision doesn't allow quote creation
          if (!existingQuote && !data.can_create_quote) {
            toast.error('Decision does not allow quote creation');
            navigate(`/enquiry/${currentEnquiryId}/decision`);
            return;
          }
        } else if (!existingQuote) {
          // No decision AND no existing quote - cannot create quote
          toast.error('Decision required before creating quote');
          navigate(`/enquiry/${currentEnquiryId}/decision`);
          return;
        }
      }
      
      // Load email history if quote exists
      if (existingQuote?.id) {
        const historyRes = await authFetch(`/quotes/${existingQuote.id}/email-history`);
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setEmailHistory(historyData.history || []);
        }
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, enquiryIdParam, quoteIdParam, navigate, terms]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate total
      if (field === 'quantity' || field === 'unit_price') {
        updated[index].total = updated[index].quantity * updated[index].unit_price;
      }
      
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { ...DEFAULT_LINE_ITEM }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const calculateVat = () => {
    return calculateSubtotal() * (vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVat();
  };

  const saveQuote = async (status = 'draft') => {
    setSaving(true);
    try {
      const payload = {
        enquiry_id: resolvedEnquiryId,
        decision_result_id: decision?.id,
        line_items: lineItems,
        vat_rate: vatRate,
        validity_days: validityDays,
        quote_number: quoteNumber || undefined,
        notes,
        terms_conditions: terms,
      };
      
      // Only include status if saving as draft (to preserve draft status)
      // When finalizing, we don't set status - the finalize endpoint handles it
      if (status === 'draft') {
        payload.status = 'draft';
      }
      
      const method = quote ? 'PATCH' : 'POST';
      const url = quote ? `/quotes/${quote.id}` : '/quotes';
      
      const res = await authFetch(url, {
        method,
        body: payload
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuote(data);
        
        // If finalizing (not just saving draft)
        if (status !== 'draft' && data.id) {
          // Use the finalize endpoint
          const finalizeRes = await authFetch(`/quotes/${data.id}/finalize`, {
            method: 'POST'
          });
          
          if (finalizeRes.ok) {
            const finalizeData = await finalizeRes.json();
            setQuote(finalizeData.quote);
            toast.success(finalizeData.message);
            
            if (finalizeData.status === 'pending_approval') {
              navigate('/approvals');
            }
          } else {
            const err = await finalizeRes.json().catch(() => ({}));
            if (err.errors) {
              toast.error(`Validation failed: ${err.errors.join(', ')}`);
            } else if (typeof err.detail === 'object' && err.detail?.errors) {
              toast.error(`Validation failed: ${err.detail.errors.join(', ')}`);
            } else {
              toast.error(err.detail || err.message || 'Failed to finalize quote');
            }
          }
        } else {
          toast.success('Quote saved as draft');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save quote');
      }
    } catch (err) {
      console.error('Quote save error:', err);
      toast.error('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const submitForApproval = async () => {
    setSubmitting(true);
    await saveQuote('pending_approval');
    setSubmitting(false);
  };

  const sendQuoteToClient = async () => {
    if (!quote?.id) {
      toast.error('Save the quote first');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Generate PDF and convert to base64
      const pdfDoc = await generateQuotePDF(quote || {
        line_items: lineItems,
        vat_rate: vatRate,
        validity_days: validityDays,
        quote_number: quoteNumber || undefined,
        notes,
        terms_conditions: terms,
        subtotal: calculateSubtotal(),
        vat_amount: calculateVat(),
        total: calculateTotal(),
        quote_number: quote?.quote_number || 'DRAFT',
        status: quote?.status || 'draft',
        created_at: quote?.created_at || new Date().toISOString(),
      }, enquiry, decision, branding);
      
      // Get PDF as base64
      const pdfBase64 = pdfDoc.output('datauristring').split(',')[1];
      
      // Send to backend
      const res = await authFetch(`/quotes/${quote.id}/send-email`, {
        method: 'POST',
        body: {
          pdf_base64: pdfBase64,
          custom_message: customMessage || null,
          include_approval_buttons: includeApprovalButtons,
          recipient_email: recipientEmail || null
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Quote sent to ${data.recipient}`);
        setShowSendModal(false);
        setSendStep(1);
        // Reload to update status
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send quote');
      }
    } catch (err) {
      console.error('Send error:', err);
      toast.error('Failed to send quote');
    } finally {
      setSendingEmail(false);
    }
  };

  const exportToPDF = async () => {
    try {
      const pdfDoc = await generateQuotePDF(quote || {
        line_items: lineItems,
        vat_rate: vatRate,
        validity_days: validityDays,
        quote_number: quoteNumber || undefined,
        notes,
        terms_conditions: terms,
        subtotal: calculateSubtotal(),
        vat_amount: calculateVat(),
        total: calculateTotal(),
        quote_number: quote?.quote_number || 'DRAFT',
        status: quote?.status || 'draft',
        created_at: quote?.created_at || new Date().toISOString(),
      }, enquiry, decision, branding);
      
      const filename = `Quote_${enquiry?.reference_number || 'draft'}_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadPDF(pdfDoc, filename);
      toast.success('Quote exported to PDF');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  };

  const markAsWon = async () => {
    if (!quote?.id) return;
    
    try {
      const res = await authFetch(`/quotes/${quote.id}/mark-won`, {
        method: 'PATCH',
        body: {
          status: 'won',
          notes: wonNotes,
          won_value: quote.total
        }
      });
      
      if (res.ok) {
        toast.success('Quote marked as WON!');
        setShowWonModal(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update quote');
      }
    } catch (err) {
      toast.error('Failed to mark quote as won');
    }
  };

  const markAsLost = async () => {
    if (!quote?.id) return;
    
    try {
      const res = await authFetch(`/quotes/${quote.id}/mark-lost`, {
        method: 'PATCH',
        body: {
          status: 'lost',
          notes: lostReason
        }
      });
      
      if (res.ok) {
        toast.success('Quote marked as lost');
        setShowLostModal(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update quote');
      }
    } catch (err) {
      toast.error('Failed to mark quote as lost');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  const canEdit = !quote || quote.status === 'draft';
  const needsApproval = decision?.requires_director_approval && user?.role !== 'director';

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="quote-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 md:px-10 py-6">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <span>{enquiry?.reference_number}</span>
                {quote && (
                  <>
                    <span>•</span>
                    <span>{quote.quote_number}</span>
                  </>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
                {quote ? 'Edit Quote' : 'Create Quote'}
              </h1>
              <p className="text-slate-500 mt-1">{enquiry?.project_name}</p>
            </div>
            
            {quote && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={exportToPDF}
                  className="rounded-full"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
                <CalendarExportButton 
                  type="quote" 
                  itemId={quote.id} 
                  label="Add Deadline"
                />
                <div className={`chip ${
                  quote.status === 'draft' ? 'chip-default' :
                  quote.status === 'pending_approval' ? 'chip-warning' :
                  quote.status === 'approved' ? 'chip-success' :
                  'chip-info'
                }`}>
                  {quote.status?.replace('_', ' ')}
                </div>
                {quote.client_response && (
                  <div className={`chip ${
                    quote.client_response === 'approved' ? 'chip-success' : 'chip-default'
                  }`} data-testid="client-response-badge">
                    Client: {quote.client_response === 'approved' ? 'Approved' : 'Not Approved'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-8 space-y-6">
        {/* Decision Validation Banner */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <div>
            <p className="font-medium text-emerald-800">Decision Result Valid</p>
            <p className="text-sm text-emerald-600">
              {decision?.outcome_label} • Decision ID: {decision?.id?.slice(0, 8)}
            </p>
          </div>
        </div>

        {/* Client & Project Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Client</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-slate-500">Name:</span> <span className="font-medium">{enquiry?.client_name}</span></p>
              <p><span className="text-slate-500">Company:</span> <span className="font-medium">{enquiry?.client_company}</span></p>
              <p><span className="text-slate-500">Email:</span> <span className="font-medium">{enquiry?.client_email}</span></p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Project</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-slate-500">Name:</span> <span className="font-medium">{enquiry?.project_name}</span></p>
              <p><span className="text-slate-500">Type:</span> <span className="font-medium uppercase">{enquiry?.project_type?.replace('_', ' ')}</span></p>
              <p><span className="text-slate-500">Location:</span> <span className="font-medium">{enquiry?.location || '-'}</span></p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Line Items</h3>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={addLineItem}
                className="rounded-full"
                data-testid="add-line-item-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Discipline</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-20">Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-20">Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-28">Unit Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-28">Total</th>
                  {canEdit && <th className="px-4 py-3 w-12"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lineItems.map((item, index) => (
                  <tr key={index} data-testid={`line-item-${index}`}>
                    <td className="px-4 py-3">
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Service description"
                        disabled={!canEdit}
                        className="border-0 bg-transparent h-9 px-0 focus-visible:ring-0"
                        data-testid={`line-item-desc-${index}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={item.discipline}
                        onValueChange={(value) => updateLineItem(index, 'discipline', value)}
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="border-0 bg-transparent h-9 px-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEP_DISCIPLINES.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        className="border-0 bg-transparent h-9 px-0 w-16 focus-visible:ring-0"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        value={item.unit}
                        onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                        disabled={!canEdit}
                        className="border-0 bg-transparent h-9 px-0 w-16 focus-visible:ring-0"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <PoundSterling className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          disabled={!canEdit}
                          className="border-0 bg-transparent h-9 pl-4 focus-visible:ring-0"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-700">
                        £{(item.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </p>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          disabled={lineItems.length === 1}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Totals */}
          <div className="border-t border-slate-100 p-6 bg-slate-50">
            <div className="max-w-xs ml-auto space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-medium">£{calculateSubtotal().toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">VAT ({vatRate}%)</span>
                <span className="font-medium">£{calculateVat().toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-lg pt-3 border-t border-slate-200">
                <span className="font-semibold text-[#1e3a5f]">Total</span>
                <span className="font-bold text-[#1e3a5f]">
                  £{calculateTotal().toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Quote Settings</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Quote Number</Label>
                <Input
                  value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)}
                  placeholder="Auto-generated if blank (e.g. QT-2026-003)"
                  disabled={!canEdit}
                  data-testid="quote-number-input"
                />
                <p className="text-xs text-slate-500">Leave blank to auto-generate. Or enter a custom reference.</p>
              </div>
              <div className="space-y-2">
                <Label>VAT Rate (%)</Label>
                <Input
                  type="number"
                  value={vatRate}
                  onChange={(e) => setVatRate(parseFloat(e.target.value) || 0)}
                  disabled={!canEdit}
                  data-testid="vat-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  value={validityDays}
                  onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
                  disabled={!canEdit}
                  data-testid="validity-input"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Notes</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes for this quote..."
              className="min-h-[120px]"
              disabled={!canEdit}
              data-testid="quote-notes-input"
            />
          </div>
        </div>

        {/* Terms */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Terms & Conditions</h3>
          <Textarea
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            className="min-h-[100px] font-mono text-sm"
            disabled={!canEdit}
            data-testid="quote-terms-input"
          />
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex flex-col md:flex-row gap-4 justify-end">
            <Button
              variant="outline"
              onClick={() => saveQuote('draft')}
              disabled={saving}
              className="rounded-full px-6"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            
            {needsApproval ? (
              <Button
                onClick={submitForApproval}
                disabled={submitting || lineItems.every(i => !i.description)}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                data-testid="submit-approval-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            ) : (
              <Button
                onClick={() => saveQuote('approved')}
                disabled={saving || lineItems.every(i => !i.description)}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                data-testid="finalize-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Finalize Quote
              </Button>
            )}
          </div>
        )}

        {/* Non-editable status */}
        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Quote {quote?.status?.replace('_', ' ')}</p>
              <p className="text-sm text-amber-600">This quote can no longer be edited</p>
            </div>
          </div>
        )}

        {/* Send to Client - Only for approved quotes */}
        {quote && quote.status === 'approved' && canSendQuote && (
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-semibold text-sky-800 mb-2">
                  Send Quote to Client
                </h3>
                <p className="text-sky-700 text-sm mb-4">
                  This quote has been approved. You can now send it to {enquiry?.client_name || 'the client'} at{' '}
                  <span className="font-medium">{enquiry?.client_email}</span>
                </p>
              </div>
              <CheckCircle2 className="w-6 h-6 text-sky-600" />
            </div>
            <Button
              onClick={() => setShowSendModal(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6"
              data-testid="send-to-client-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Client
            </Button>
          </div>
        )}

        {/* Quote Already Sent */}
        {quote && quote.status === 'sent' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-semibold text-emerald-800 mb-2">
                  Quote Sent
                </h3>
                <p className="text-emerald-700 text-sm mb-2">
                  This quote was sent to {quote.sent_to || enquiry?.client_email} on{' '}
                  {quote.sent_at ? new Date(quote.sent_at).toLocaleDateString('en-GB') : 'N/A'}
                </p>
                {canSendQuote && (
                  <Button
                    onClick={() => setShowSendModal(true)}
                    variant="outline"
                    className="mt-2 rounded-full text-sm"
                    size="sm"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Resend
                  </Button>
                )}
              </div>
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        )}

        {/* Email Send History */}
        {emailHistory.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Email History</h3>
            <div className="space-y-2">
              {emailHistory.map((log, idx) => (
                <div key={log.id || idx} className={`flex items-center justify-between p-3 rounded-xl ${log.success ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <div className="flex items-center gap-3">
                    {log.success ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                    <div>
                      <div className="text-sm font-medium text-slate-700">
                        Sent to {log.recipient_email}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(log.sent_at).toLocaleString('en-GB')} by {log.sent_by_name}
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs font-medium ${log.success ? 'text-emerald-700' : 'text-red-700'}`}>
                    {log.success ? 'Delivered' : 'Failed'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Won/Lost Actions for sent or approved quotes */}
        {quote && ['sent', 'approved'].includes(quote.status) && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">Quote Outcome</h3>
            <p className="text-slate-600 text-sm mb-4">
              Has the client responded to this quote? Update the outcome to track your win rate.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowWonModal(true)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="mark-won-btn"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Mark as WON
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowLostModal(true)}
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                data-testid="mark-lost-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Mark as Lost
              </Button>
            </div>
          </div>
        )}

        {/* Won Status Display */}
        {quote?.status === 'won' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold text-emerald-800 text-lg">Quote WON!</p>
                <p className="text-sm text-emerald-600">
                  Won on {quote.won_date ? new Date(quote.won_date).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>
            </div>
            {quote.won_notes && (
              <p className="text-emerald-700 text-sm mt-2 p-3 bg-emerald-100 rounded-xl">
                {quote.won_notes}
              </p>
            )}
            <p className="text-emerald-800 font-semibold mt-3">
              Won Value: £{(quote.won_value || quote.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </p>
          </div>
        )}

        {/* Lost Status Display */}
        {quote?.status === 'lost' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                <XCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold text-red-800 text-lg">Quote Lost</p>
                <p className="text-sm text-red-600">
                  Lost on {quote.lost_at ? new Date(quote.lost_at).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>
            </div>
            {quote.lost_reason && (
              <p className="text-red-700 text-sm mt-2 p-3 bg-red-100 rounded-xl">
                <strong>Reason:</strong> {quote.lost_reason}
              </p>
            )}
          </div>
        )}
      </main>

      {/* Send to Client Modal — 2-Step */}
      <Dialog open={showSendModal} onOpenChange={(open) => { setShowSendModal(open); if (!open) setSendStep(1); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-sky-700">
              <Send className="w-5 h-5" />
              {sendStep === 1 ? 'Send Quote to Client' : 'Confirm & Send'}
            </DialogTitle>
          </DialogHeader>

          {sendStep === 1 ? (
            /* Step 1: Details */
            <div className="space-y-4 pt-2">
              <div className="p-4 bg-sky-50 rounded-xl border border-sky-200">
                <p className="text-sky-800 text-sm font-medium mb-2">Recipient</p>
                <p className="text-sm font-semibold text-sky-900">{enquiry?.client_name || 'Client'}</p>
                <div className="mt-2">
                  <Label className="text-xs text-sky-700">Email Address</Label>
                  <Input
                    type="email"
                    value={recipientEmail || enquiry?.client_email || ''}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="mt-1 bg-white"
                    data-testid="recipient-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-semibold">Email Message</Label>
                <p className="text-xs text-slate-500 mb-1.5">Edit the message that will appear in the email body</p>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Enter the message to include in the email..."
                  className="mt-1"
                  rows={4}
                  data-testid="custom-message-input"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Include Approval Buttons</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Client can approve or decline directly from the email</p>
                </div>
                <button
                  onClick={() => setIncludeApprovalButtons(!includeApprovalButtons)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${includeApprovalButtons ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  data-testid="approval-buttons-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${includeApprovalButtons ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSendModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setSendStep(2)}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-full"
                  disabled={!(recipientEmail || enquiry?.client_email)}
                >
                  Next — Review
                </Button>
              </div>
            </div>
          ) : (
            /* Step 2: Confirm */
            <div className="space-y-4 pt-2">
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
                <p className="text-amber-900 font-semibold text-sm mb-3">Please confirm you want to send this quote:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-amber-700">Quote:</span>
                    <span className="font-semibold text-amber-900">{quote?.quote_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">To:</span>
                    <span className="font-semibold text-amber-900">{recipientEmail || enquiry?.client_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Total:</span>
                    <span className="font-semibold text-amber-900">£{calculateTotal().toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Approval Buttons:</span>
                    <span className={`font-semibold ${includeApprovalButtons ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {includeApprovalButtons ? 'Included' : 'Not Included'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800 text-xs font-medium">
                  This action cannot be undone. The quote PDF will be emailed to the recipient and logged in the audit trail.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSendStep(1)}
                  className="flex-1 rounded-full"
                  disabled={sendingEmail}
                >
                  Back
                </Button>
                <Button
                  onClick={sendQuoteToClient}
                  disabled={sendingEmail}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-full"
                  data-testid="confirm-send-btn"
                >
                  {sendingEmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Confirm & Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Won Modal */}
      <Dialog open={showWonModal} onOpenChange={setShowWonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-emerald-700">
              <Trophy className="w-5 h-5" />
              Mark Quote as Won
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <p className="text-emerald-800 text-sm">
                Congratulations! You are about to mark this quote as won.
              </p>
              <p className="text-lg font-bold text-emerald-900 mt-2">
                Value: £{(quote?.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Notes (optional)</Label>
              <Textarea
                value={wonNotes}
                onChange={(e) => setWonNotes(e.target.value)}
                placeholder="Any notes about winning this quote..."
                className="mt-1.5"
                data-testid="won-notes-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowWonModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={markAsWon}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full"
                data-testid="confirm-won-btn"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Confirm Won
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lost Modal */}
      <Dialog open={showLostModal} onOpenChange={setShowLostModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-700">
              <XCircle className="w-5 h-5" />
              Mark Quote as Lost
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-red-800 text-sm">
                Understanding why quotes are lost helps improve future win rates.
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Reason for Loss</Label>
              <Textarea
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                placeholder="Why was this quote not accepted? (e.g., price, timing, competitor)"
                className="mt-1.5"
                data-testid="lost-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLostModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={markAsLost}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                data-testid="confirm-lost-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Mark as Lost
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
